/**
 * A FALHA DE CARGA EM PORTUGUÊS — e a regra de que o VAZIO não fala sobre ela.
 *
 * ---------------------------------------------------------------------------
 * OS DOIS DEFEITOS QUE ESTE MÓDULO EXISTE PARA FECHAR
 *
 * **1. O token cru.** Três telas de frota faziam `setError(String(e))` sobre um
 * `new Error('HTTP 403')` e imprimiam o resultado:
 *
 *     Erro ao carregar telemetria: Error: HTTP 403      (Telemetria)
 *     Error: HTTP 403                                   (Manobrista)
 *     · Error: HTTP 403                                 (Mapa)
 *
 * `Error: HTTP 403` não é frase: é o `toString()` de um objeto de exceção, com
 * o nome da classe colado. É o mesmo defeito que `cco-avisos.ts` acabou de
 * encerrar no CCO com os tokens de aviso, na véspera, em outro vocabulário.
 *
 * **2. O vazio que não consulta o erro — e este é o grave.** O Mapa media
 * `!loading && resumo.total === 0` e escrevia:
 *
 *     "Nenhum ônibus visível para a fonte de telemetria ativa · Error: HTTP 403
 *      ... confira se o serviço de sincronia está classificando a procedência."
 *
 * Três falsidades numa frase: afirma o conjunto VAZIO sobre um pedido que foi
 * RECUSADO (não houve resposta para contar), imprime o token, e manda
 * investigar o serviço de sincronia, que não é a causa. Com 403 não se sabe
 * quantos ônibus existem — e "nenhum" é uma afirmação sobre o mundo.
 *
 * É a mesma classe que o A2 fechou no CCO (impossibilidade apresentada como
 * espera), reaberta no Mapa: **estado de falha apresentado como estado de
 * dado**. `estadoDaLista` existe para que a pergunta seja feita UMA vez e na
 * ordem certa.
 *
 * NADA AQUI CONHECE React, DOM OU `fetch`.
 */

/** Uma falha de carregamento, já classificada. */
export interface FalhaDeCarga {
  /** O status HTTP, ou `'rede'` quando o pedido nem chegou a ter resposta. */
  status: number | 'rede';
}

/**
 * Extrai o status de um erro lançado por `fetch` + `throw new Error('HTTP n')`.
 *
 * Aceita a forma que as telas usam hoje e cai para `'rede'` em qualquer outra —
 * inclusive `TypeError: Failed to fetch`, que é o caso em que não houve
 * resposta nenhuma e portanto não há status a nomear.
 */
export function falhaDe(e: unknown): FalhaDeCarga {
  const texto = e instanceof Error ? e.message : String(e ?? '');
  const m = /(?:^|\s)(\d{3})(?:\s|$)/.exec(texto);
  if (m === null) return { status: 'rede' };
  const n = Number(m[1]);
  return Number.isFinite(n) ? { status: n } : { status: 'rede' };
}

/**
 * A frase da falha, em português e SEM o token.
 *
 * `assunto` é o que não carregou, em minúsculas e no meio da frase ("a
 * telemetria da frota", "o mapa"). Cada família de status pede uma AÇÃO
 * diferente, e é por isso que elas não colapsam numa frase só:
 *
 *   403  o acesso desta conta não alcança o recorte. Não adianta recarregar, e
 *        não é falha do serviço — quem resolve é quem administra o acesso.
 *   401  a sessão caiu. Entrar de novo resolve.
 *   404  a rota não existe nesta versão — descompasso de deploy.
 *   429  excesso de requisições. TRANSITÓRIO, mas a ação é ESPERAR, não repetir
 *        em rajada — e por isso ela não cai no mesmo balde dos 5xx.
 *   5xx  o servidor falhou. TRANSITÓRIO: tentar de novo é a ação certa.
 *   rede não houve resposta. Também transitório, e a causa é do lado de cá.
 *
 * A de 403 é a única que NÃO manda tentar de novo, e é justamente a que estava
 * sendo apresentada como lista vazia.
 *
 * O RAMO GENÉRICO NÃO IMPRIME MAIS O NÚMERO NU — e esse era o buraco que o
 * teste deixava: ele proibia `HTTP` e `Error`, mas liberava o inteiro. `(resposta
 * 429 do servidor)` é a mesma classe de token cru que a frente inteira persegue,
 * só com o dígito solto. Um 4xx fora dos nomeados acima é raro e inacionável
 * para o operador, então a frase diz o que ele pode fazer (falar com suporte),
 * não o código — que vai para o LOG, não para a tela.
 */
export function fraseDeFalhaDeCarga(falha: FalhaDeCarga, assunto: string): string {
  if (falha.status === 'rede') {
    return `Não foi possível falar com o servidor para carregar ${assunto}. Verifique a conexão e tente de novo.`;
  }
  if (falha.status === 403) {
    return `Seu acesso não alcança ${assunto}. Isto não é uma falha e recarregar não muda — fale com quem administra as permissões da sua conta.`;
  }
  if (falha.status === 401) {
    return `Sua sessão expirou. Entre de novo para carregar ${assunto}.`;
  }
  if (falha.status === 404) {
    return `O portal pediu ${assunto} numa rota que o servidor não reconhece. Provavelmente há um descompasso de versão entre portal e servidor.`;
  }
  if (falha.status === 429) {
    return `O servidor está recebendo pedidos demais e recusou este. Aguarde alguns instantes antes de carregar ${assunto} de novo.`;
  }
  if (falha.status >= 500) {
    return `O servidor falhou ao devolver ${assunto}. É transitório — tente de novo em alguns instantes.`;
  }
  return `Não foi possível carregar ${assunto}. Se persistir, fale com o suporte.`;
}

/**
 * O ESTADO DA LISTA, com a falha ANTES do vazio.
 *
 * A ordem é o conserto: `'falha'` vem antes de `'vazio'` porque **um pedido
 * recusado não tem conjunto para contar**. Perguntar `total === 0` primeiro é o
 * que fazia a tela afirmar "nenhum ônibus visível" sobre um 403.
 *
 * `'carregando'` antes de tudo pela mesma razão que o esqueleto do CCO: antes
 * da resposta não há nem falha nem vazio a declarar.
 */
export type EstadoDaLista = 'carregando' | 'falha' | 'vazio' | 'preenchida';

export function estadoDaLista(p: {
  carregando: boolean;
  falha: FalhaDeCarga | null;
  total: number;
}): EstadoDaLista {
  if (p.carregando) return 'carregando';
  if (p.falha !== null) return 'falha';
  return p.total === 0 ? 'vazio' : 'preenchida';
}
